import React from 'react';
import { useTranslation } from 'react-i18next';
import { Zap, CreditCard, TrendingUp, Infinity } from 'lucide-react';
import { SpaceCard, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useCredits } from '../../hooks/useCredits';
import { MixedText } from '../../utils/rtl';
import { stripeApi } from '../../services/api';

interface CreditStatusCardProps {
  onUpgradeClick: () => void;
}

// Credit costs for different operations (matching backend CREDIT_COSTS)
const CREDIT_COSTS = {
  study_session_creation: 10,  // Lecture Summary
  document_upload: 1,          // Document Summary  
  exam_processing: 30,         // AI Exam Solution & Generation
  material_regeneration: 5,    // Re-requests for Materials
  question_regeneration: 5,    // Questions (for free tier limit)
};

const CreditStatusCard: React.FC<CreditStatusCardProps> = ({ onUpgradeClick }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [isCreatingPortal, setIsCreatingPortal] = React.useState(false);
  const {
    creditStatus,
    subscriptionPlans,
    loading: creditsLoading,
    createSubscriptionCheckout
  } = useCredits();

  // Handle upgrade button click
  const handleUpgrade = async () => {
    if (creditStatus?.user_tier === 'free') {
      onUpgradeClick(); // Show upgrade modal
    } else {
      // Premium users go to billing portal via API
      setIsCreatingPortal(true);
      try {
        const data = await stripeApi.createPortalSession();
        window.open(data.url, '_blank');
      } catch (error) {
        console.error('Error creating portal session:', error);
      } finally {
        setIsCreatingPortal(false);
      }
    }
  };

  // Calculate available actions based on current credits
  const calculateAvailableActions = () => {
    if (!creditStatus) return null;

    const credits = creditStatus.credits_remaining;
    const tier = creditStatus.user_tier;
    const isUnlimited = creditStatus.is_unlimited;

    // If user is unlimited, all actions are unlimited
    if (isUnlimited) {
      return {
        lectureSummary: {
          available: Infinity,
          total: Infinity,
          cost: 0,
          unlimited: true
        },
        documentSummary: {
          available: Infinity,
          total: Infinity,
          cost: 0,
          unlimited: true
        },
        examSolution: {
          available: Infinity,
          total: Infinity,
          cost: 0,
          unlimited: true
        },
        examGeneration: {
          available: Infinity,
          total: Infinity,
          cost: 0,
          unlimited: true,
          disabled: false
        },
        materialRegeneration: {
          available: Infinity,
          total: Infinity,
          cost: 0,
          unlimited: true
        },
        questions: {
          available: Infinity,
          total: Infinity,
          cost: 0,
          unlimited: true
        }
      };
    }

    return {
      lectureSummary: {
        available: Math.floor(credits / CREDIT_COSTS.study_session_creation),
        total: tier === 'free' ? 3 : 40,
        cost: CREDIT_COSTS.study_session_creation,
        unlimited: false
      },
      documentSummary: {
        available: Math.floor(credits / CREDIT_COSTS.document_upload),
        total: tier === 'free' ? 30 : 400, // Assuming 1:1 ratio for docs
        cost: CREDIT_COSTS.document_upload,
        unlimited: false
      },
      examSolution: {
        available: Math.floor(credits / CREDIT_COSTS.exam_processing),
        total: tier === 'free' ? 1 : Math.floor(400 / CREDIT_COSTS.exam_processing),
        cost: CREDIT_COSTS.exam_processing,
        unlimited: false
      },
      examGeneration: {
        available: Math.floor(credits / CREDIT_COSTS.exam_processing),
        total: tier === 'free' ? 0 : Math.floor(400 / CREDIT_COSTS.exam_processing),
        cost: CREDIT_COSTS.exam_processing,
        unlimited: false,
        disabled: tier === 'free'
      },
      materialRegeneration: {
        available: Math.floor(credits / CREDIT_COSTS.material_regeneration),
        total: tier === 'free' ? 6 : 80,
        cost: CREDIT_COSTS.material_regeneration,
        unlimited: false
      },
      questions: {
        available: tier === 'premium' ? Infinity : Math.floor(credits / CREDIT_COSTS.question_regeneration),
        total: tier === 'premium' ? Infinity : 6,
        cost: tier === 'premium' ? 0 : CREDIT_COSTS.question_regeneration,
        unlimited: tier === 'premium'
      }
    };
  };

  // Get credit color based on percentage remaining
  const getCreditColor = () => {
    if (!creditStatus) return 'text-surface-muted';
    if (creditStatus.is_unlimited) return 'text-vibrant-orange';
    
    const percentage = creditStatus.credits_remaining / creditStatus.monthly_credits_limit;
    if (percentage <= 0.1) return 'text-red-500';
    if (percentage <= 0.3) return 'text-orange-500';
    return 'text-vibrant-orange';
  };


  // Get current plan info
  const getCurrentPlan = () => {
    if (!subscriptionPlans || !creditStatus) return null;
    return subscriptionPlans.find(plan => plan.id === creditStatus.user_tier);
  };

  if (creditsLoading) {
    return (
      <SpaceCard constellation="cassiopeia" className="group hover:shadow-xl transition-all duration-300">
        <CardContent className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" className="text-vibrant-orange" />
          <span className="ml-3 text-surface-primary">
            <MixedText>{isRTL ? 'טוען נתוני קרדיטים...' : 'Loading credit data...'}</MixedText>
          </span>
        </CardContent>
      </SpaceCard>
    );
  }

  if (!creditStatus) {
    return (
      <SpaceCard constellation="cassiopeia" className="group hover:shadow-xl transition-all duration-300">
        <CardContent className={`text-center py-8 text-surface-secondary ${isRTL ? 'text-right' : ''}`}>
          {isRTL ? 'לא ניתן לטעון מידע על קרדיטים' : 'Unable to load credit information'}
        </CardContent>
      </SpaceCard>
    );
  }

  const currentPlan = getCurrentPlan();
  const actions = calculateAvailableActions();

  return (
    <SpaceCard constellation="cassiopeia" className="group hover:shadow-xl transition-all duration-300">
      <CardHeader>
        <CardTitle className={`text-xl ${isRTL ? 'text-right' : ''}`}>
          <MixedText>{isRTL ? 'מצב מנוי וקרדיטים' : 'Subscription & Credits'}</MixedText>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Plan Header */}
        <div className={`flex items-center justify-center p-4 rounded-lg border ${
          creditStatus.is_unlimited 
            ? 'bg-gradient-to-r from-purple-500/20 to-vibrant-orange/20 border-purple-500/30' 
            : 'bg-gradient-to-r from-vibrant-orange/10 to-vibrant-orange/20 border-vibrant-orange/20'
        }`}>
          <div className="flex items-center space-x-3">
            {creditStatus.is_unlimited && (
              <Infinity className="w-6 h-6 text-purple-500" />
            )}
            <div className="text-center">
              <div className="font-semibold text-surface-primary">
                {creditStatus.is_unlimited
                  ? (isRTL ? 'מנוי בלתי מוגבל' : 'Unlimited')
                  : currentPlan?.name || (creditStatus.user_tier === 'free' 
                    ? (isRTL ? 'תלמיד חינם' : 'Free Tier')
                    : (isRTL ? 'תלמיד פרימיום' : 'Premium Student')
                  )
                }
              </div>
              <div className="text-sm text-surface-secondary">
                {creditStatus.is_unlimited
                  ? (isRTL ? 'ללא הגבלה' : 'No Limits')
                  : creditStatus.subscription_status === 'active' 
                    ? (isRTL ? 'פעיל' : 'Active')
                    : (isRTL ? 'לא פעיל' : 'Inactive')
                }
              </div>
            </div>
          </div>
        </div>

        {/* Large Credit Display */}
        <div className="text-center py-6">
          {creditStatus.is_unlimited ? (
            <>
              <div className="flex items-center justify-center mb-2">
                <Infinity className="w-16 h-16 text-purple-500" />
              </div>
              <div className="text-surface-secondary mb-4">
                <MixedText>{isRTL ? 'קרדיטים בלתי מוגבלים' : 'Unlimited Credits'}</MixedText>
              </div>
            </>
          ) : (
            <>
              <div className={`text-5xl font-bold ${getCreditColor()} mb-2`}>
                <MixedText>{Math.floor(creditStatus.credits_remaining).toString()}</MixedText>
              </div>
              <div className="text-surface-secondary mb-4">
                <MixedText>{isRTL ? 'קרדיטים זמינים' : 'Credits Available'}</MixedText>
              </div>
              
              {/* Credit progress bar */}
              <div className="w-full max-w-xs mx-auto mb-2">
                <div className="w-full bg-surface-muted/20 rounded-full h-2 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-vibrant-orange to-vibrant-orange/80 rounded-full transition-all duration-500 ease-out"
                    style={{ 
                      width: `${Math.min(100, (creditStatus.credits_remaining / creditStatus.monthly_credits_limit) * 100)}%` 
                    }}
                  />
                </div>
              </div>
              
              <div className="text-xs text-surface-muted">
                <MixedText>
                  {`${creditStatus.monthly_credits_limit} ${isRTL ? 'קרדיטים לחודש' : 'credits per month'}`}
                </MixedText>
              </div>
            </>
          )}
        </div>

        {/* Features Grid */}
        {actions && (
          <div className="grid grid-cols-2 gap-4">
            {/* Row 1: Lecture Summary */}
            <div className="p-4 bg-surface-muted/20 rounded-lg border border-surface-muted/30 hover:border-vibrant-orange/30 transition-colors">
              <h4 className={`font-medium text-surface-primary mb-2 ${isRTL ? 'text-right' : ''}`}>
                <MixedText>{isRTL ? 'סיכום הרצאות' : 'Lecture Summary'}</MixedText>
              </h4>
              <div className={`text-sm ${isRTL ? 'text-right' : ''}`}>
                {actions.lectureSummary.unlimited ? (
                  <>
                    <div className="flex items-center gap-1 text-purple-500 font-bold">
                      <Infinity className="w-4 h-4" />
                      <MixedText>{isRTL ? 'בלתי מוגבל' : 'Unlimited'}</MixedText>
                    </div>
                    <div className="text-surface-muted">
                      <MixedText>{isRTL ? 'ללא עלות' : 'No cost'}</MixedText>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={`font-bold ${actions.lectureSummary.available > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      <MixedText>
                        {`${actions.lectureSummary.available} ${isRTL ? 'זמין' : 'available'}`}
                      </MixedText>
                    </div>
                    <div className="text-surface-muted">
                      <MixedText>{`${actions.lectureSummary.cost} ${isRTL ? 'קרדיטים' : 'credits'}`}</MixedText>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Row 1: Document Summary */}
            <div className="p-4 bg-surface-muted/20 rounded-lg border border-surface-muted/30 hover:border-vibrant-orange/30 transition-colors">
              <h4 className={`font-medium text-surface-primary mb-2 ${isRTL ? 'text-right' : ''}`}>
                <MixedText>{isRTL ? 'סיכום מסמכים' : 'Document Summary'}</MixedText>
              </h4>
              <div className={`text-sm ${isRTL ? 'text-right' : ''}`}>
                {actions.documentSummary.unlimited ? (
                  <>
                    <div className="flex items-center gap-1 text-purple-500 font-bold">
                      <Infinity className="w-4 h-4" />
                      <MixedText>{isRTL ? 'בלתי מוגבל' : 'Unlimited'}</MixedText>
                    </div>
                    <div className="text-surface-muted">
                      <MixedText>{isRTL ? 'ללא עלות' : 'No cost'}</MixedText>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={`font-bold ${actions.documentSummary.available > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      <MixedText>
                        {`${actions.documentSummary.available} ${isRTL ? 'זמין' : 'available'}`}
                      </MixedText>
                    </div>
                    <div className="text-surface-muted">
                      <MixedText>{`${actions.documentSummary.cost} ${isRTL ? 'קרדיט' : 'credit'}`}</MixedText>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Row 2: AI Exam Solution */}
            <div className="p-4 bg-surface-muted/20 rounded-lg border border-surface-muted/30 hover:border-vibrant-orange/30 transition-colors">
              <h4 className={`font-medium text-surface-primary mb-2 ${isRTL ? 'text-right' : ''}`}>
                <MixedText>{isRTL ? 'פתרון מבחנים AI' : 'AI Exam Solution'}</MixedText>
              </h4>
              <div className={`text-sm ${isRTL ? 'text-right' : ''}`}>
                {actions.examSolution.unlimited ? (
                  <>
                    <div className="flex items-center gap-1 text-purple-500 font-bold">
                      <Infinity className="w-4 h-4" />
                      <MixedText>{isRTL ? 'בלתי מוגבל' : 'Unlimited'}</MixedText>
                    </div>
                    <div className="text-surface-muted">
                      <MixedText>{isRTL ? 'ללא עלות' : 'No cost'}</MixedText>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={`font-bold ${actions.examSolution.available > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      <MixedText>
                        {`${actions.examSolution.available} ${isRTL ? 'זמין' : 'available'}`}
                      </MixedText>
                    </div>
                    <div className="text-surface-muted">
                      <MixedText>{`${actions.examSolution.cost} ${isRTL ? 'קרדיטים' : 'credits'}`}</MixedText>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Row 2: AI Exam Generation */}
            <div className={`p-4 bg-surface-muted/20 rounded-lg border border-surface-muted/30 hover:border-vibrant-orange/30 transition-colors ${actions.examGeneration.disabled && !actions.examGeneration.unlimited ? 'opacity-50' : ''}`}>
              <h4 className={`font-medium text-surface-primary mb-2 ${isRTL ? 'text-right' : ''}`}>
                <MixedText>{isRTL ? 'יצירת מבחנים AI' : 'AI Exam Generation'}</MixedText>
              </h4>
              <div className={`text-sm ${isRTL ? 'text-right' : ''}`}>
                {actions.examGeneration.unlimited ? (
                  <>
                    <div className="flex items-center gap-1 text-purple-500 font-bold">
                      <Infinity className="w-4 h-4" />
                      <MixedText>{isRTL ? 'בלתי מוגבל' : 'Unlimited'}</MixedText>
                    </div>
                    <div className="text-surface-muted">
                      <MixedText>{isRTL ? 'ללא עלות' : 'No cost'}</MixedText>
                    </div>
                  </>
                ) : actions.examGeneration.disabled ? (
                  <div className="text-orange-500 font-bold">
                    <MixedText>{isRTL ? 'לא זמין - שדרג' : 'Upgrade Required'}</MixedText>
                  </div>
                ) : (
                  <>
                    <div className={`font-bold ${actions.examGeneration.available > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      <MixedText>
                        {`${actions.examGeneration.available} ${isRTL ? 'זמין' : 'available'}`}
                      </MixedText>
                    </div>
                    <div className="text-surface-muted">
                      <MixedText>{`${actions.examGeneration.cost} ${isRTL ? 'קרדיטים' : 'credits'}`}</MixedText>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Next Reset Info */}
        {creditStatus.next_reset && !creditStatus.is_unlimited && (
          <div className={`text-xs text-surface-secondary pt-3 border-t border-surface-muted/20 ${isRTL ? 'text-right' : ''}`}>
            <MixedText>
              {isRTL 
                ? `איפוס הבא: ${new Date(creditStatus.next_reset).toLocaleDateString('he-IL')}`
                : `Resets: ${new Date(creditStatus.next_reset).toLocaleDateString('en-US')}`
              }
            </MixedText>
          </div>
        )}

        {/* Main CTA Button */}
        <div className="pt-4">
          {!creditStatus.is_unlimited && (
            <Button
              onClick={handleUpgrade}
              variant={creditStatus.user_tier === 'free' ? 'primary' : 'secondary'}
              size="lg"
              className={`w-full font-semibold ${creditStatus.user_tier === 'free' 
                ? 'bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 hover:from-vibrant-orange/90 hover:to-vibrant-orange text-white shadow-lg' 
                : ''
              }`}
              icon={creditStatus.user_tier === 'free' ? <Zap className="w-5 h-5" /> : <CreditCard className="w-5 h-5" />}
              disabled={isCreatingPortal}
            >
              {isCreatingPortal ? (
                <LoadingSpinner size="sm" className="text-white" />
              ) : (
                creditStatus.user_tier === 'free' 
                  ? (isRTL ? 'שדרג לפרימיום' : 'Upgrade to Premium')
                  : (isRTL ? 'נהל מנוי' : 'Manage Subscription')
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </SpaceCard>
  );
};

export default CreditStatusCard;